import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
	children: React.ReactNode;
	className?: string;
	dataLength: number;
	lengthPerPage?: number;
	loadMore(nextPage: number): void;
	parentElement: React.MutableRefObject<HTMLDivElement | null>;
	treshold?: number;
}

const InfiniteScroll: React.FC<Props> = ({
	children,
	className,
	dataLength,
	lengthPerPage = 6,
	loadMore: loadMoreExt,
	parentElement,
	treshold = 250,
}) => {
	// Refs
	const scrollElement = useRef<HTMLDivElement | null>(null);

	// States
	const [page, setPage] = useState<number>(0);

	// Private functions
	const hasMore = useCallback(() => {
		const pages = dataLength / lengthPerPage;
		const pagesTrunc = Math.trunc(pages);
		const totalPages = pages - pagesTrunc > 0 ? pagesTrunc + 1 : pagesTrunc;

		return page < totalPages;
	}, [dataLength, lengthPerPage, page]);

	const loadMore = useCallback(() => {
		if (hasMore()) {
			setPage((prev) => prev + 1);
			loadMoreExt(page + 1);
		}
	}, [hasMore, loadMoreExt, page]);

	// Listeners
	const detachScrollListener = useCallback(() => {
		if (scrollElement.current) {
			scrollElement.current.removeEventListener('scroll', scrollListener);
			scrollElement.current.removeEventListener('resize', scrollListener);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const scrollListener = useCallback(() => {
		if (scrollElement.current && parentElement.current) {
			const offset =
				scrollElement.current.scrollHeight -
				parentElement.current.scrollTop -
				parentElement.current.clientHeight;

			if (offset < treshold && scrollElement.current.offsetParent !== null) {
				detachScrollListener();
				loadMore();
			}
		}
	}, [detachScrollListener, loadMore, parentElement, treshold]);

	// Effects
	useEffect(() => {
		if (parentElement.current) {
			const el = parentElement.current;

			el.addEventListener('scroll', scrollListener);
			el.addEventListener('resize', scrollListener);

			return () => {
				el.removeEventListener('scroll', scrollListener);
				el.removeEventListener('resize', scrollListener);
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parentElement, scrollListener]);

	useEffect(() => {
		loadMore();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={className} ref={scrollElement}>
			{children}
		</div>
	);
};

export default InfiniteScroll;
